import { Dispatch, SetStateAction, use, useEffect, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { DeviceType } from 'shared/models/device-type';
import ObjectSort from 'shared/models/object-sort';
import Pagination from 'shared/models/pagination';

import { useReadFilterValuesMutation } from 'features/device/domain/queries/device-queries';
import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import ReadLinkableDevicesCommand from 'features/device/linkable-device/domain/models/read-linkable-devices-command';
import { useReadLinkableTransmitters } from 'features/device/linkable-device/domain/queries/linkable-device-queries';
import ReadLinkableTransmittersResponse from 'features/device/linkable-transmitters/domain/models/read-linkable-transmitters-response';
import DeviceFilter from 'features/device/models/device-filter';
import DeviceFilterValue, { FilterValueType } from 'features/device/models/device-filter-value';
import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';

type LinkableTransmittersTable = {
  readLinkableDevicesData?: ReadLinkableTransmittersResponse;
  readLinkableDevicesIsSuccess: boolean;
  readLinkableDevicesIsLoading: boolean;
  currentPage: number;
  currentRowsPerPage: number;
  currentFilters: DeviceFilter[];
  search: string;
  sort: ObjectSort<LinkableDevice>;
  getFilterValues: () => DeviceFilterValue[];
  handleOnPageChanged: (newPage: number) => void;
  handleOnRowsPerPageChanged: (rowsPerPage: number) => void;
  handleOnSortChanged: (property: NestedKeyof<LinkableDevice>, isAscending: boolean) => void;
  onSearchChanged: (searchQuery: string) => void;
  clearFilters: () => void;
  setSearch: Dispatch<SetStateAction<string>>;
  handleOnOptionsSelected: (filterValueType: FilterValueType, keys: string[]) => void;
};

type Props = {
  organisationUnitId: string;
  devicesToHide: LinkableDevice[];
  deviceIdToSelect?: string;
};

const useLinkableTransmittersTable = ({ organisationUnitId, devicesToHide, deviceIdToSelect }: Props): LinkableTransmittersTable => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const {
    mutate: readLinkableDevices,
    data: readLinkableDevicesData,
    isSuccess: readLinkableDevicesIsSuccess,
    isPending: readLinkableDevicesIsLoading,
    error: readLinkableDevicesError,
  } = useReadLinkableTransmitters();

  const [sort, setSort] = useState<ObjectSort<LinkableDevice>>({
    property: 'name',
    isAscending: true,
  });
  const [linkableDeviceFilters, setLinkableDeviceFilters] = useState<DeviceFilter[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentFilters, setCurrentFilters] = useState<DeviceFilter[]>(linkableDeviceFilters);
  const [selectDeviceId, setSelectDeviceId] = useState<string>();

  const { data: readFilterValuesData, mutate: triggerReadFilterValuesQuery } = useReadFilterValuesMutation();
  const { handleSelectedDevicesChanged } = useDeviceLinkContextProvider();

  const {
    currentPagination: [currentPagination, setCurrentPagination],
    currentSearchQuery: [currentSearchQuery],
    currentPage,
    currentRowsPerPage,
    setCurrentPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSearchChanged,
  } = useTableFiltering();

  const getFilterValues = (): DeviceFilterValue[] => {
    const allowedDeviceTypes = new Set([
      DeviceType.G1,
      DeviceType.G2,
      DeviceType.G2General,
      DeviceType.DesoResidentTransmitter,
      DeviceType.DesoClient,
      DeviceType.UniversalAlarmTransmitter,
    ]);

    const filterValues = readFilterValuesData?.filterValues ?? [];
    const filteredValues = filterValues
      .filter((item) => item.filterValueType === FilterValueType.Type)
      .flatMap((item) => item.values)
      .filter((value) => allowedDeviceTypes.has(value.key as DeviceType));

    return [
      {
        filterValueType: FilterValueType.Type,
        values: filteredValues,
      },
    ];
  };

  const handleOnSortChanged = (property: NestedKeyof<LinkableDevice>, isAscending: boolean): void => {
    setSort({
      isAscending: isAscending,
      property: property,
    });
    setCurrentPage(0);
    setCurrentPagination({
      skip: 0,
      take: currentRowsPerPage,
    });
  };

  const onSearchChanged = (searchQuery: string): void => {
    handleOnSearchChanged(searchQuery);
    handleSelectedDevicesChanged([]);
  };

  const handleOnOptionsSelected = (filterValueType: FilterValueType, keys: string[]): void => {
    const newFilters = [...currentFilters];
    const indexOfFilterWithSameFilterValueType = newFilters.findIndex((filter) => filter.filterValueType === filterValueType);
    const deviceFilter: DeviceFilter = {
      filterValueType: filterValueType,
      values: keys,
    };

    if (indexOfFilterWithSameFilterValueType === -1) {
      newFilters.push(deviceFilter);
    } else {
      newFilters[indexOfFilterWithSameFilterValueType] = deviceFilter;
    }

    handleOnFiltersChanged(newFilters);
  };

  const handleOnFiltersChanged = (deviceFilter: DeviceFilter[]): void => {
    setLinkableDeviceFilters(deviceFilter);
    setCurrentFilters(deviceFilter);
    setCurrentPage(0);
    handleOnPageChanged(0);
  };

  const clearFilters = (): void => {
    setLinkableDeviceFilters([]);
    setCurrentFilters([]);
    onSearchChanged('');
    setSearch('');
  };

  useEffect(() => {
    if (deviceIdToSelect) {
      setSelectDeviceId(deviceIdToSelect);
    }
  }, [deviceIdToSelect]);

  useEffect(() => {
    if (readLinkableDevicesError) {
      setErrorMessage(readLinkableDevicesError);
    }
  }, [readLinkableDevicesError, setErrorMessage]);

  useEffect(() => {
    const paginationWithHiddenDevices = {
      skip: currentPagination.skip,
      take: currentPagination.take + devicesToHide.length,
    } as Pagination;

    const readDevicesCommand = {
      organisationUnitId: organisationUnitId,
      pagination: paginationWithHiddenDevices,
      sort: sort,
      filters: currentFilters,
      searchQuery: search,
    } as ReadLinkableDevicesCommand;

    readLinkableDevices(readDevicesCommand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, currentPagination, currentFilters, currentSearchQuery, selectDeviceId]);

  useEffect(() => {
    if (!selectDeviceId || !readLinkableDevicesData?.devices) return;

    const deviceToSelect = readLinkableDevicesData.devices.find((d) => d.id === selectDeviceId);

    if (deviceToSelect) {
      handleSelectedDevicesChanged([deviceToSelect]);
      setSelectDeviceId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readLinkableDevicesData, selectDeviceId]);

  useEffect(() => {
    const readFilterValuesCommand = {
      organisationUnitId: organisationUnitId,
    };
    triggerReadFilterValuesQuery(readFilterValuesCommand);
  }, [organisationUnitId, triggerReadFilterValuesQuery]);

  return {
    readLinkableDevicesData,
    readLinkableDevicesIsSuccess,
    readLinkableDevicesIsLoading,
    currentPage,
    currentRowsPerPage,
    currentFilters,
    search,
    sort,
    getFilterValues,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSortChanged,
    onSearchChanged,
    clearFilters,
    setSearch,
    handleOnOptionsSelected,
  };
};

export default useLinkableTransmittersTable;
